import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Check, LayoutGrid, List, Sparkles } from "lucide-react";
import { AVAILABLE_WIDGETS, WIDGET_CATEGORIES, WidgetConfig } from "./DashboardWidgetConfig";
import { cn } from "@/lib/utils";

interface AddWidgetDialogProps {
  visibleWidgets: string[];
  onAddWidget: (widgetId: string) => void;
  onRemoveWidget: (widgetId: string) => void;
}

type ViewMode = "grid" | "list";

export function AddWidgetDialog({ visibleWidgets, onAddWidget, onRemoveWidget }: AddWidgetDialogProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [hoveredWidget, setHoveredWidget] = useState<string | null>(null);
  
  const isWidgetVisible = (widgetId: string) => visibleWidgets.includes(widgetId);

  const handleToggleWidget = (widget: WidgetConfig) => {
    if (isWidgetVisible(widget.id)) {
      onRemoveWidget(widget.id);
    } else {
      onAddWidget(widget.id);
    }
  };

  const filteredWidgets = selectedCategory === "all" 
    ? AVAILABLE_WIDGETS 
    : AVAILABLE_WIDGETS.filter(w => w.category === selectedCategory);

  const activeCount = visibleWidgets.length;
  const totalCount = AVAILABLE_WIDGETS.length;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Widget
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Widget Gallery
              </DialogTitle>
              <DialogDescription className="mt-1">
                Browse and manage dashboard widgets. {activeCount} of {totalCount} widgets active.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 pt-4 pb-2 border-b bg-muted/30">
            <TabsList className="h-auto flex-wrap gap-2 bg-transparent p-0">
              {WIDGET_CATEGORIES.map((category) => {
                const count = category.id === "all" 
                  ? AVAILABLE_WIDGETS.length 
                  : AVAILABLE_WIDGETS.filter(w => w.category === category.id).length;
                return (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-4"
                  >
                    {category.label}
                    <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                      {count}
                    </Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <ScrollArea className="flex-1 p-6">
            <TabsContent value={selectedCategory} className="mt-0">
              {viewMode === "grid" ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {filteredWidgets.map((widget) => (
                    <WidgetPreviewCard
                      key={widget.id}
                      widget={widget}
                      isVisible={isWidgetVisible(widget.id)}
                      isHovered={hoveredWidget === widget.id}
                      onHover={setHoveredWidget}
                      onToggle={() => handleToggleWidget(widget)}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredWidgets.map((widget) => (
                    <WidgetListItem
                      key={widget.id}
                      widget={widget}
                      isVisible={isWidgetVisible(widget.id)}
                      onToggle={() => handleToggleWidget(widget)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

interface WidgetPreviewCardProps {
  widget: WidgetConfig;
  isVisible: boolean;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  onToggle: () => void;
}

function WidgetPreviewCard({ widget, isVisible, isHovered, onHover, onToggle }: WidgetPreviewCardProps) {
  const Icon = widget.icon;
  
  return (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 overflow-hidden group",
        isVisible && "ring-2 ring-primary",
        isHovered && "shadow-lg scale-[1.02]"
      )}
      onClick={onToggle}
      onMouseEnter={() => onHover(widget.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Preview area */}
      <div className={cn(
        "h-24 relative bg-gradient-to-br",
        widget.previewColor
      )}>
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="h-10 w-10 text-foreground/20" />
        </div>
        
        {/* Mini preview elements */}
        <div className="absolute inset-2 flex flex-col gap-1.5 opacity-60">
          {widget.category === "statistics" && (
            <>
              <div className="h-2 w-12 bg-foreground/10 rounded" />
              <div className="h-6 w-16 bg-foreground/15 rounded" />
            </>
          )}
          {widget.category === "analytics" && (
            <div className="flex-1 flex items-end gap-1 pt-4">
              {[40, 65, 45, 80, 55, 70].map((h, i) => (
                <div 
                  key={i} 
                  className="flex-1 bg-foreground/15 rounded-t"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          )}
          {widget.category === "activity" && (
            <div className="space-y-1.5 pt-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-foreground/15" />
                  <div className="h-1.5 flex-1 bg-foreground/10 rounded" />
                </div>
              ))}
            </div>
          )}
          {widget.category === "tools" && (
            <div className="grid grid-cols-2 gap-1.5 pt-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-4 bg-foreground/10 rounded" />
              ))}
            </div>
          )}
        </div>

        {/* Status indicator */}
        {isVisible && (
          <div className="absolute top-2 right-2 h-6 w-6 bg-primary rounded-full flex items-center justify-center shadow-md">
            <Check className="h-3.5 w-3.5 text-primary-foreground" />
          </div>
        )}
      </div>

      {/* Content */}
      <CardHeader className="p-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-medium truncate">{widget.title}</CardTitle>
            <Badge variant="outline" className="mt-1 text-[10px] px-1.5 py-0 capitalize">
              {widget.category}
            </Badge>
          </div>
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
      </CardHeader>
      
      <CardContent className="p-3 pt-0">
        <CardDescription className="text-xs line-clamp-2">
          {widget.description}
        </CardDescription>
      </CardContent>

      {/* Hover overlay */}
      <div className={cn(
        "absolute inset-0 bg-primary/5 opacity-0 transition-opacity pointer-events-none",
        isHovered && "opacity-100"
      )} />
    </Card>
  );
}

interface WidgetListItemProps {
  widget: WidgetConfig;
  isVisible: boolean;
  onToggle: () => void;
}

function WidgetListItem({ widget, isVisible, onToggle }: WidgetListItemProps) {
  const Icon = widget.icon;
  
  return (
    <div
      className={cn(
        "flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md",
        isVisible && "ring-2 ring-primary bg-primary/5"
      )}
      onClick={onToggle}
    >
      {/* Preview thumbnail */}
      <div className={cn(
        "h-14 w-14 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br",
        widget.previewColor
      )}>
        <Icon className="h-6 w-6 text-foreground/40" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium truncate">{widget.title}</h4>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize shrink-0">
            {widget.category}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
          {widget.description}
        </p>
      </div>

      {/* Status */}
      <div className={cn(
        "h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors",
        isVisible ? "bg-primary text-primary-foreground" : "bg-muted"
      )}>
        {isVisible ? (
          <Check className="h-4 w-4" />
        ) : (
          <Plus className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </div>
  );
}
